import posthog from "posthog-js";

export interface LoginResponse {
  access_token: string;
  token_type: string;
}

export interface User {
  id: string;
  email: string;
  is_active: boolean;
  is_superuser: boolean;
  is_verified: boolean;
}

const TOKEN_KEY = 'auth_token';
const IS_ANONYMOUS_KEY = 'is_anonymous';
const USER_ID_KEY = 'user_id';

// Event system for auth state changes
type AuthStateChangeCallback = (isLoggedIn: boolean) => void;
const authStateChangeCallbacks: AuthStateChangeCallback[] = [];

export class AuthService {
  private static token: string | null = null;

  private static getStoredToken(): string | null {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(TOKEN_KEY);
  }

  private static setStoredToken(
    token: string | null,
    isAnonymous: boolean = false,
  ): void {
    if (typeof window === 'undefined') return;
    if (token) {
      localStorage.setItem(TOKEN_KEY, token);
      localStorage.setItem(IS_ANONYMOUS_KEY, isAnonymous.toString());
    } else {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(IS_ANONYMOUS_KEY);
    }
    // Notify all listeners of the auth state change
    this.notifyAuthStateChange(!!token && !isAnonymous);
  }

  private static notifyAuthStateChange(isLoggedIn: boolean): void {
    authStateChangeCallbacks.forEach((callback) => callback(isLoggedIn));
  }

  static onAuthStateChange(callback: AuthStateChangeCallback): () => void {
    authStateChangeCallbacks.push(callback);
    // Return unsubscribe function
    return () => {
      const index = authStateChangeCallbacks.indexOf(callback);
      if (index !== -1) {
        authStateChangeCallbacks.splice(index, 1);
      }
    };
  }

  static isAnonymous(): boolean {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem(IS_ANONYMOUS_KEY) === 'true';
  }

  static async ensureToken(): Promise<void> {
    if (this.getStoredToken()) return;

    try {
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_API_BASE_URL}/api/auth/anonymous`,
        {
          method: 'POST',
        },
      );

      if (!response.ok) {
        throw new Error('Failed to get anonymous token');
      }

      const data = await response.json();
      this.setStoredToken(data.token, true);
    } catch (error) {
      console.error('Error getting anonymous token:', error);
    }
  }

  static async signup(email: string, password: string) {
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_API_BASE_URL}/api/auth/register`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          password,
          is_active: true,
          is_superuser: false,
          is_verified: false,
        }),
      },
    );

    if (!response.ok) {
      throw new Error('Failed to sign up');
    }

    // After successful signup, automatically log in the user
    return this.login(email, password);
  }

  static async login(email: string, password: string) {
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_API_BASE_URL}/api/auth/jwt/login`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: `username=${encodeURIComponent(email)}&password=${encodeURIComponent(password)}`,
      },
    );

    if (!response.ok) {
      throw new Error('Invalid email or password');
    }

    const data = await response.json();
    this.setStoredToken(data.access_token, false);
    return data;
  }

  static googleLogin() {
    // Open Google OAuth login in a new window
    window.open(
      `${process.env.NEXT_PUBLIC_API_BASE_URL}/api/auth/google/login`,
      '_self',
    );
  }

  static async logout() {
    const token = this.getStoredToken();
    if (!token) return;

    try {
      // First, clear the current token
      this.setStoredToken(null);

      // Then try to logout from the backend
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_API_BASE_URL}/api/auth/jwt/logout`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      // Regardless of the backend response, get a new anonymous token
      await this.ensureToken();
    } catch (error) {
      console.error('Error during logout:', error);
      // Even if there's an error, ensure we have an anonymous token
      await this.ensureToken();
    }
  }

  static async getCurrentUser() {
    const token = this.getStoredToken();
    if (!token) {
      throw new Error('Not logged in');
    }

    const response = await fetch(
      `${process.env.NEXT_PUBLIC_API_BASE_URL}/api/users/me`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );

    if (!response.ok) {
      throw new Error('Failed to get current user');
    }

    const userData = await response.json();
    
    // Store user ID for later use
    if (typeof window !== 'undefined' && userData.id) {
      localStorage.setItem(USER_ID_KEY, userData.id);
    }
    
    return userData;
  }

  static getToken() {
    return this.getStoredToken();
  }

  static setToken(token: string): void {
    this.setStoredToken(token);
  }

  static isLoggedIn(): boolean {
    return !!this.getStoredToken();
  }
  
  static getUserId(): string | null {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(USER_ID_KEY);
  }
}
